import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

type City = { id: string; name: string };
type Period = "weekly" | "monthly" | "all_time";

type Row = {
  rank: number;
  score: number;
  user_id: string;
  username: string;
  display_name: string;
  avatar_url: string | null;
  total_badges: number;
  is_current_user: boolean;
};

const RANK_COLOURS: Record<number, string> = {
  1: "#F59E0B",
  2: "#9CA3AF",
  3: "#B45309",
};
const MEDAL: Record<number, string> = { 1: "🥇", 2: "🥈", 3: "🥉" };

export default function Leaderboard() {
  const { user } = useAuth();
  const [cities, setCities] = useState<City[]>([]);
  const [cityId, setCityId] = useState<string | null>(null);
  const [period, setPeriod] = useState<Period>("weekly");
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [meRow, setMeRow] = useState<Row | null>(null);

  // Load cities + default to user's home city
  useEffect(() => {
    (async () => {
      const [{ data: cityRows }, profRes] = await Promise.all([
        supabase
          .from("cities")
          .select("id, name")
          .eq("is_active", true)
          .order("name"),
        user
          ? supabase
              .from("profiles")
              .select("home_city_id")
              .eq("id", user.id)
              .maybeSingle()
          : Promise.resolve({ data: null } as any),
      ]);
      const list = (cityRows ?? []) as City[];
      setCities(list);
      const homeId = (profRes as any)?.data?.home_city_id as string | undefined;
      const london = list.find((c) => c.name === "London");
      setCityId(homeId ?? london?.id ?? list[0]?.id ?? null);
    })();
  }, [user]);

  const loadRows = useCallback(
    async (cid: string, per: Period) => {
      setLoading(true);
      const { data } = await supabase.rpc("get_leaderboard", {
        city_id_param: cid,
        period_param: per,
        limit_param: 50,
      });
      const list = ((data ?? []) as Row[]).slice();
      setRows(list);

      // If current user not in top 50, fetch their row separately
      if (user) {
        const inList = list.some((r) => r.user_id === user.id);
        if (!inList) {
          const { data: mineAll } = await supabase.rpc("get_leaderboard", {
            city_id_param: cid,
            period_param: per,
            limit_param: 10000,
          });
          const mine =
            ((mineAll ?? []) as Row[]).find((r) => r.user_id === user.id) ?? null;
          setMeRow(mine);
        } else {
          setMeRow(null);
        }
      } else {
        setMeRow(null);
      }
      setLoading(false);
    },
    [user],
  );

  // On home city load: force recalc for all three periods, then load.
  const forcedCitiesRef = useState(() => new Set<string>())[0];
  useEffect(() => {
    if (!cityId) return;
    let cancelled = false;
    (async () => {
      if (!forcedCitiesRef.has(cityId)) {
        forcedCitiesRef.add(cityId);
        await Promise.all(
          (["weekly", "monthly", "all_time"] as Period[]).map((p) =>
            supabase.rpc("calculate_leaderboard", {
              city_id_param: cityId,
              period_param: p,
            }),
          ),
        );
        if (cancelled) return;
        await loadRows(cityId, period);
        return;
      }
      await loadRows(cityId, period);
      const { data: refreshed } = await supabase.rpc(
        "refresh_leaderboard_if_stale",
        { city_id_param: cityId, period_param: period },
      );
      if (!cancelled && refreshed === true) {
        await loadRows(cityId, period);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [cityId, period, loadRows, forcedCitiesRef]);


  return (
    <div className="pt-2">
      <header className="px-4 h-14 flex items-center">
        <h1 className="text-[22px] font-bold">Leaderboard</h1>
      </header>

      {/* City selector */}
      <div className="overflow-x-auto px-4 pb-3">
        <div className="flex gap-2 min-w-min">
          {cities.map((c) => {
            const active = c.id === cityId;
            return (
              <button
                key={c.id}
                onClick={() => setCityId(c.id)}
                className="shrink-0 h-9 px-4 rounded-[var(--radius-full)] text-[14px] font-medium transition-colors"
                style={{
                  backgroundColor: active
                    ? "var(--colour-accent)"
                    : "var(--colour-surface-raised)",
                  color: active
                    ? "var(--colour-text-inverse)"
                    : "var(--colour-text-primary)",
                }}
              >
                {c.name}
              </button>
            );
          })}
        </div>
      </div>

      {/* Period tabs */}
      <div className="px-4 border-b" style={{ borderColor: "var(--colour-border)" }}>
        <div className="flex">
          {(["weekly", "monthly", "all_time"] as Period[]).map((p) => {
            const active = p === period;
            return (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                className="flex-1 h-11 text-[14px] font-medium relative"
                style={{
                  color: active
                    ? "var(--colour-text-primary)"
                    : "var(--colour-text-secondary)",
                }}
              >
                {p === "weekly" ? "Weekly" : p === "monthly" ? "Monthly" : "All Time"}
                {active && (
                  <span
                    className="absolute bottom-0 left-1/4 right-1/4 h-[2px] rounded-full"
                    style={{ backgroundColor: "var(--colour-accent)" }}
                  />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* List */}
      <div className="px-4 mt-3 pb-8">
        {loading ? (
          <ul className="space-y-2">
            {Array.from({ length: 10 }).map((_, i) => (
              <li
                key={i}
                className="h-[60px] rounded-[var(--radius-md)] animate-pulse"
                style={{ backgroundColor: "var(--colour-surface-raised)" }}
              />
            ))}
          </ul>
        ) : rows.length === 0 ? (
          <div className="text-center mt-10 px-6">
            <div className="text-[56px] mb-2" aria-hidden>🏆</div>
            <h3 className="text-[18px] font-bold">
              Be the first in {cities.find((c) => c.id === cityId)?.name ?? "your city"}
            </h3>
            <p className="text-[14px] text-[var(--colour-text-secondary)] mt-1">
              Log a meal to claim the top spot.
            </p>
          </div>
        ) : (
          <>
            <ul className="space-y-2">
              {rows.map((r) => (
                <li key={r.user_id}>
                  <LeaderboardRow row={r} />
                </li>
              ))}
            </ul>
            {meRow && (
              <>
                <div
                  className="my-3 border-t border-dashed"
                  style={{ borderColor: "var(--colour-border)" }}
                />
                <LeaderboardRow row={meRow} />
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function LeaderboardRow({ row }: { row: Row }) {
  const colour = RANK_COLOURS[row.rank];
  const medal = MEDAL[row.rank];
  return (
    <Link
      to={`/u/${row.username}`}
      className="flex items-center gap-3 px-3 h-[60px] rounded-[var(--radius-md)] border active:opacity-80 transition-opacity"
      style={{
        borderColor: "var(--colour-border)",
        backgroundColor: row.is_current_user
          ? "color-mix(in srgb, var(--colour-accent) 10%, var(--colour-surface))"
          : "var(--colour-surface)",
      }}
    >
      <div
        className="w-10 shrink-0 text-center font-bold"
        style={{
          color: colour ?? "var(--colour-text-secondary)",
          fontSize: row.rank <= 3 ? "18px" : "15px",
        }}
      >
        {medal ? `${medal}` : row.rank}
      </div>
      {row.avatar_url ? (
        <img
          src={row.avatar_url}
          alt={row.display_name}
          className="w-8 h-8 rounded-[var(--radius-full)] object-cover shrink-0"
        />
      ) : (
        <div
          className="w-8 h-8 rounded-[var(--radius-full)] shrink-0 flex items-center justify-center text-[12px] font-semibold"
          style={{
            backgroundColor: "var(--colour-surface-raised)",
            color: "var(--colour-text-secondary)",
          }}
        >
          {row.display_name?.slice(0, 1).toUpperCase()}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <p className="text-[14px] font-semibold truncate">{row.display_name}</p>
        <p className="text-[12px] text-[var(--colour-text-secondary)] truncate">
          @{row.username}
        </p>
      </div>
      <div className="text-right shrink-0">
        <div
          className="text-[15px] font-bold leading-none"
          style={{ color: "var(--colour-accent)" }}
        >
          {row.score}
        </div>
        <div className="text-[11px] text-[var(--colour-text-secondary)] mt-1">visits</div>
      </div>
      {row.is_current_user && (
        <span
          className="ml-2 px-2 py-0.5 rounded-[var(--radius-full)] text-[11px] font-semibold"
          style={{
            backgroundColor: "var(--colour-accent)",
            color: "var(--colour-text-inverse)",
          }}
        >
          You
        </span>
      )}
    </Link>
  );
}
